import { useState } from 'react'
import { FileDownload } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedSummaryEvent } from '@/analytics/utils/medsummary-analytics'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { downloadMedSummaryAssetExcel } from '../fetch/download-med-summary-asset-excel'

interface MedSummaryAssetDownloadButtonProps {
  assetId: string
  /** Mobile top-bar presentation: plain icon sized for the bar rather than the outlined chip. */
  compact?: boolean
}

/**
 * Downloads the living billing summary as an Excel file.
 */
export default function MedSummaryAssetDownloadButton(props: MedSummaryAssetDownloadButtonProps) {
  const { assetId, compact = false } = props
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const [isLoading, setIsLoading] = useState(false)
  const title = isLoading ? 'Download in progress for Medical Billing Summary' : 'Download the Medical Billing Summary'

  const handleDownload = async () => {
    if (isLoading) return
    setIsLoading(true)
    trackMedSummaryEvent(AnalyticsEvent.MedSummaryAssetDownloadClicked, { medSummaryAssetId: assetId }, trackEvent)
    try {
      await downloadMedSummaryAssetExcel(assetId, 'Medical Billing Summary.xlsx')
    } catch (error) {
      // A failed download used to leave the button silently idle, so the user had no way to tell
      // a slow download from a dead one. Match the chronology and timeline behavior.
      console.error('Error downloading billing summary Excel:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Medical Billing Summary download failed. Please try again.',
          durationMs: kToastDurationMediumMs,
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (compact) {
    return (
      <IconButton
        aria-label={title}
        color="primary"
        icon={<FileDownload />}
        loading={isLoading}
        onClick={handleDownload}
        size="lg"
        title={title}
        variant="plain"
      />
    )
  }

  return <IconButton aria-label={title} icon={<FileDownload />} loading={isLoading} onClick={handleDownload} size="sm" title={title} variant="outlined" />
}
